import { Divider, Space } from "antd";
import type { SorterResult } from "antd/es/table/interface";
import { isValidElement, type ReactNode, useCallback, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { DataTableCopyableCell } from "~components/DataTable/components/DataTableCopyableCell/DataTableCopyableCell.tsx";
import { TableRowActionDelete } from "~components/DataTable/components/TableRowActionDelete/TableRowActionDelete.tsx";
import type { BaseRowActions } from "~components/DataTable/types/baseRowActions.ts";
import type { CustomRowActions } from "~components/DataTable/types/customRowActions.ts";
import type { DataTableColumn } from "~components/DataTable/types/dataTableColumn.ts";
import type { TableRowActionsMenuItem } from "~components/DataTable/types/tableRowActionsMenuItem.ts";
import { getDataTableColumns } from "~components/DataTable/utils/getDataTableColumns.ts";
import { useUserPermissions } from "~context/UserPermissionsContext";
import { type PermissionNames, PermissionOperations } from "~enums/permissions.ts";
import type { DataTableColumnGroupDefinition } from "~types/dataTableColumnGroupDefinition.ts";
import { TableRowActionsMenu } from "../components/TableRowActionsMenu/TableRowActionsMenu";

type ColumnChooserConfig = {
  columnChooserLocalStorageKey: string;
  tableConfiguration: Array<DataTableColumnGroupDefinition>;
};

type ColumnSortConfig<T> = {
  sorterLocalStorageKey: string;
  defaultSorter?: SorterResult<T>;
  onSortChange?: (value: string) => void;
};

// Stopping the click on the cell keeps it from reaching the row navigation handler
const withRowNavigation = <T,>(column: DataTableColumn<T>): DataTableColumn<T> => {
  if (!column.disableRowNavigation && !column.copyable) {
    return column;
  }

  return {
    ...column,
    onCell: (record, index) => {
      const cellProps = column.onCell?.(record, index) ?? {};

      return {
        ...cellProps,
        style: { ...cellProps.style, cursor: "auto" },
        onClick: (event) => event.stopPropagation(),
        onAuxClick: (event) => event.stopPropagation(),
        onMouseDown: (event) => event.stopPropagation(),
      };
    },
  };
};

const isCellContent = (content: unknown): content is ReactNode =>
  content === null || content === undefined || typeof content !== "object" || Array.isArray(content) || isValidElement(content);

const withCopyableCell = <T,>(column: DataTableColumn<T>, massEditMode: boolean): DataTableColumn<T> => {
  if (!column.copyable || massEditMode) {
    return column;
  }

  const { getValueToCopy } = column;

  return {
    ...column,
    render: (value, record, index) => {
      const content = column.render?.(value, record, index) ?? value;

      if (!isCellContent(content)) {
        return content;
      }

      const copyValue = getValueToCopy ? getValueToCopy(record) : value?.toString();

      return <DataTableCopyableCell copyValue={copyValue}>{content}</DataTableCopyableCell>;
    },
  };
};

type TableColumnsConfig<T> = {
  columnChooserConfig: ColumnChooserConfig;
  columnSortConfig?: ColumnSortConfig<T>;
  tableColumns: DataTableColumn<T>[];
  rowNameKey: string;
  rowActions?: {
    base?: BaseRowActions<T>;
    custom?: CustomRowActions<T>;
  };
  permissionNames: Array<PermissionNames>;
  massEditMode?: boolean;
};

export const useTableColumns = <T,>(config: TableColumnsConfig<T>) => {
  const { formatMessage } = useIntl();
  const initialSelectedColumns = getDataTableColumns(
    config.columnChooserConfig.columnChooserLocalStorageKey,
    config.columnChooserConfig.tableConfiguration,
  );

  const { hasEveryPermission } = useUserPermissions();

  const handleSortChange = useCallback(
    (sorter: SorterResult<T>) => {
      if (config.columnSortConfig?.onSortChange) {
        const columnDataIndex = sorter.column?.dataIndex || sorter.columnKey;
        //Take last element of dataIndex array to get the actual column name for sorting
        const sortField = Array.isArray(columnDataIndex) ? columnDataIndex[columnDataIndex.length - 1] : columnDataIndex;
        const sorterOrder = sorter?.order;
        const order = sorterOrder === "descend" ? "-" : "";
        const result = sortField ? `${order}${sortField}` : "";
        config.columnSortConfig.onSortChange(result);
      }
      setSortConfig(sorter);
    },
    [config.columnSortConfig],
  );

  const [selectedColumns, setSelectedColumns] = useState(initialSelectedColumns);
  const [sortConfig, setSortConfig] = useState<SorterResult<T> | SorterResult<T>[]>(() => {
    if (config.columnSortConfig) {
      const saved = localStorage.getItem(config.columnSortConfig.sorterLocalStorageKey);
      if (saved) {
        return JSON.parse(saved);
      }

      return config.columnSortConfig.defaultSorter || {};
    }
  });

  useEffect(() => {
    if (config.columnSortConfig) {
      const saved = localStorage.getItem(config.columnSortConfig.sorterLocalStorageKey);
      let sortConfig = config.columnSortConfig.defaultSorter || {};
      if (saved) {
        sortConfig = JSON.parse(saved);
      }
      handleSortChange(sortConfig);
    }
  }, [handleSortChange, config.columnSortConfig?.defaultSorter, config.columnSortConfig?.sorterLocalStorageKey, config.columnSortConfig]);

  const setSortColumn = (sorter: SorterResult<T> | SorterResult<T>[]) => {
    if (!Array.isArray(sorter) && config.columnSortConfig) {
      handleSortChange(sorter);
      localStorage.setItem(config.columnSortConfig.sorterLocalStorageKey, JSON.stringify(sorter));
    }
  };

  const plainColumnsConfig = config.columnChooserConfig.tableConfiguration.flatMap((group) => group.columns).filter((column) => column.key);

  const allColumns = Object.fromEntries(config.tableColumns.map((column) => [column.key, column]));
  const persistentColumns = config.tableColumns.filter((x) => x.persistent);

  const filteredByConfigColumns = plainColumnsConfig
    .map((column) => ({
      ...allColumns[column.key],
      title: column.titleIntlKey ? formatMessage({ id: column.titleIntlKey }) : allColumns[column.key]?.title,
    }))
    .filter((x) => selectedColumns.includes(JSON.stringify(x.key))) as DataTableColumn<T>[];

  const mergedColumns = [...filteredByConfigColumns, ...persistentColumns];

  const columnsWithSorting = mergedColumns.map((column) => ({
    ...column,
    ...(Array.isArray(sortConfig)
      ? sortConfig.find((sort) => sort.columnKey === column.key)?.order && {
          defaultSortOrder: sortConfig.find((sort) => sort.columnKey === column.key)?.order,
        }
      : sortConfig?.columnKey === column.key && {
          defaultSortOrder: sortConfig?.order,
        }),
  }));

  const checkPermission = (operation: PermissionOperations) =>
    hasEveryPermission(config.permissionNames.map((permissionName) => ({ permissionName, permissionOperations: [operation] })));

  const rowActions = config.rowActions?.base || {};
  const customRowActions = config.rowActions?.custom || {};

  const actionsLength = Object.keys(rowActions).length;
  const customActionsLength = Object.keys(customRowActions).length;

  if (actionsLength > 0 || customActionsLength > 0) {
    columnsWithSorting.push({
      align: "right",
      persistent: true,
      fixed: "right",
      width: 10,
      disableRowNavigation: true,
      render: (row) => {
        const tableRowActionMenuItems: TableRowActionsMenuItem[] = [];
        if (
          rowActions?.view &&
          !(rowActions?.view?.hidden && rowActions?.view?.hidden?.(row)) &&
          checkPermission(PermissionOperations.READ)
        ) {
          tableRowActionMenuItems.push({
            key: "details",
            label: (
              <Space onClick={() => rowActions?.view?.onClick?.(row)}>
                <i className="fas fa-eye mr-2" />
                <span>{formatMessage({ id: rowActions?.view?.intlKey ?? "details" })}</span>
              </Space>
            ),
          });
        }
        if (
          rowActions?.manage &&
          !(rowActions?.manage?.hidden && rowActions?.manage?.hidden?.(row)) &&
          checkPermission(PermissionOperations.UPDATE)
        ) {
          tableRowActionMenuItems.push({
            key: "manage",
            label: (
              <Space onClick={() => rowActions?.manage?.onClick?.(row)}>
                <i className="fas fa-eye mr-2" />
                <span>{formatMessage({ id: rowActions?.manage?.intlKey ?? "manage" })}</span>
              </Space>
            ),
          });
        }
        Object.entries(customRowActions).forEach(([key, action]) => {
          if (!action || action.hidden?.(row)) {
            return;
          }
          tableRowActionMenuItems.push({
            key: key,
            disabled: action.disabled?.(row),
            label: <>{action.renderAction?.(row)}</>,
          });
        });
        if (
          rowActions?.delete &&
          !(rowActions?.delete?.hidden && rowActions?.delete?.hidden?.(row)) &&
          checkPermission(PermissionOperations.DELETE)
        ) {
          if (actionsLength > 1) {
            tableRowActionMenuItems.push({
              key: "divider-1",
              label: <Divider style={{ margin: 0 }} />,
            });
          }
          tableRowActionMenuItems.push({
            key: "delete",
            label: (
              <TableRowActionDelete
                textIntlKey={rowActions?.delete?.intlKey ?? "delete"}
                handleDelete={() => {
                  rowActions?.delete?.onClick?.(row);
                }}
                itemName={rowActions?.delete?.itemName?.(row) || row[config.rowNameKey]}
                loading={rowActions?.delete?.loading}
              />
            ),
          });
        }

        return <TableRowActionsMenu tableRowActionsMenuItems={tableRowActionMenuItems} />;
      },
    });
  }

  const columnsToShow = columnsWithSorting.map(withRowNavigation).map((column) => withCopyableCell(column, !!config.massEditMode));

  return { columnsToShow, setSelectedColumns, setSortColumn };
};
